import React from "react"
import { XIcon } from "lucide-react"
import { toast, type ExternalToast } from "sonner"

import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"

import {
  CircleAlert,
  CircleCheck,
  Info,
  Loader2,
  TriangleAlert,
} from "lucide-react"

type SonnerVariant = "success" | "info" | "error" | "warning" | "loading" | "custom"

function ToastIcon({ variant }: { variant: SonnerVariant }) {
  switch (variant) {
    case "success":
      return (
        <CircleCheck className="tw:size-5 tw:shrink-0 tw:fill-primary tw:text-primary-foreground" />
      )
    case "info":
      return (
        <Info className="tw:size-5 tw:shrink-0 tw:fill-primary tw:text-primary-foreground" />
      )
    case "error":
      return (
        <CircleAlert className="tw:size-5 tw:shrink-0 tw:fill-destructive tw:text-white" />
      )
    case "warning":
      return (
        <TriangleAlert className="tw:size-5 tw:shrink-0 tw:fill-amber-500 tw:text-white" />
      )
    case "loading":
      return (
        <Loader2 className="tw:size-5 tw:shrink-0 tw:animate-spin tw:text-muted-foreground" />
      )
    case "custom":
      return(
        <WarningIcon/>
      )
  }
}

type ToastAction = {
  label: React.ReactNode
  onClick?: () => void
}

type CustomToastProps = {
  id: string | number
  title: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
  action?: ToastAction
  closeButton?: boolean
  onDismiss?: () => void
  variant: SonnerVariant
}

// Filled amber alert disk from the Figma design. The global Toaster maps
// `warning` to an outline TriangleAlertIcon, so this lives here to keep the
// custom toast self-contained without touching that shared config.
function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn("tw:size-5 tw:shrink-0 tw:text-warning", className)}
    >
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path d="M12 7v6" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1.25" fill="white" />
    </svg>
  )
}

// Mirrors Figma node 28195:654596: icon + title/description stack + optional
// outline action button, all on the popover surface.
function CustomToast({ id, title, description, icon, action, closeButton, onDismiss, variant }: CustomToastProps) {
  return (
    // Outer wrapper is unclipped and padded on the top/right so the
    // corner-floating close button has room to overhang the inner surface.
    // The inner surface keeps overflow-hidden + rounded-md for clean corners;
    // nothing square ever pokes past the rounded border because the button
    // lives in the padding, outside the clipped surface.
    <div className="cn-custom-toast tw:relative tw:pt-2.5 tw:pr-2.5 tw:font-sans">
      {closeButton ? (
        // Mirrors sonner's native close button: a small circular control
        // centered on the inner surface's top-right corner, using the same
        // surface/border tokens. top-2.5/right-2.5 aligns it with that corner
        // (matching the wrapper padding), then the translate halves overhang it.
        <button
          type="button"
          aria-label="Close"
          className="tw:absolute tw:top-2.5 tw:right-2.5 tw:z-10 tw:flex tw:size-5 tw:translate-x-1/2 tw:-translate-y-1/2 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-border tw:bg-popover tw:text-popover-foreground tw:transition tw:hover:bg-accent tw:hover:text-accent-foreground"
          onClick={() => toast.dismiss(id)}
        >
          <XIcon className="tw:size-3" />
        </button>
      ) : null}

      <div className="cn-custom-toast-surface tw:flex tw:w-full tw:items-center tw:gap-2 tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-popover tw:p-4">
        {icon ?? <ToastIcon variant={variant}/> }

        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:items-start tw:gap-0.5 tw:text-sm tw:break-words">
          <p className="tw:w-full tw:font-medium tw:text-popover-foreground">{title}</p>
          {description ? (
            <p className="tw:w-full tw:text-muted-foreground">{description}</p>
          ) : null}
        </div>

        {action ? (
          <Button
            variant="outline"
            size="xs"
            className="tw:shrink-0 tw:rounded-[4px] tw:text-secondary-foreground tw:drop-shadow-xs"
            onClick={() => {
              action.onClick?.()
              toast.dismiss(id)
            }}
          >
            {action.label}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

type CustomToastOptions = Omit<CustomToastProps, "id"> & {
  duration?: number,
  dismissible?: boolean,
  position?: ExternalToast["position"],
  closeButton?: boolean,
  onDismiss?: () => void,
  variant: string
}

// Renders the toast through sonner's custom slot so it bypasses the global
// Toaster's variant/icon styling entirely.
function customToast({ duration, position, dismissible, closeButton, onDismiss, variant, ...props }: CustomToastOptions) {
  // sonner's `closeButton` option only renders for its built-in variants, not
  // toast.custom — so render our own close button inside CustomToast instead.
  return toast.custom((id) => <CustomToast id={id} closeButton={closeButton} variant={variant} {...props} />, { duration, position, dismissible, onDismiss })
}

export { CustomToast, customToast }
